// src/components/agent/parts/ToolCallChip.js
//
// Renders a `tool_call` ViewPart (frontend spec §8.6): pending spinner /
// executing pulse / success tick / error icon from TYPED state — no
// status-string sniffing (v1 wart #2). Handles live (reducer) and doc parts
// identically because both are ViewParts. Restyled for the page surface
// (§3.4): background.alt chip.
import React from 'react';
import { Chip, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { toolLabel } from './toolLabels';

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'pulsing',
})(({ theme, pulsing }) => ({
  margin: theme.spacing(0.5, 0),
  backgroundColor: theme.palette.background.alt,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: '100%',
  height: 'auto',
  minHeight: 24,
  alignSelf: 'flex-start',
  '& .MuiChip-label': {
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    textOverflow: 'clip',
    paddingTop: 3,
    paddingBottom: 3,
  },
  '& .MuiChip-icon': {
    color: theme.palette.text.secondary,
  },
  ...(pulsing && {
    animation: 'chipPulse 1.6s ease-in-out infinite',
    '@keyframes chipPulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.55 },
    },
  }),
}));

/**
 * part: a `tool_call` ViewPart.
 * turnStopped: the turn this call belongs to has stopped without finishing it
 *   (a crashed turn whose lease expired, or a user-requested stop), so no
 *   result will ever arrive for an unfinished call.
 */
function ToolCallChip({ part, turnStopped = false }) {
  const { isError, denied, completed, executing, statusMessage, name, backgroundJob } = part;
  // An unfinished call on a stopped turn is shown as interrupted rather than
  // left spinning forever (X13-9). Denied and errored calls are already
  // finished, so they keep their own marks.
  const interrupted = turnStopped && !completed && !isError && !denied;

  // A background launch: the call's one answer is the job envelope, so the
  // chip says what actually happened rather than the tool's normal status.
  if (backgroundJob && completed && !isError) {
    return (
      <StyledChip
        icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
        label={`Background job submitted: ${toolLabel(name)}`}
        size="small"
        pulsing={false}
      />
    );
  }

  let icon;
  if (denied) {
    // A call the policy or the person refused: neither a success nor a
    // failure of the tool, so it gets its own mark.
    icon = <BlockIcon sx={{ color: 'warning.main !important' }} />;
  } else if (isError) {
    icon = <ErrorIcon sx={{ color: 'error.main !important' }} />;
  } else if (completed) {
    icon = <DoneIcon sx={{ color: 'success.main !important' }} />;
  } else if (interrupted) {
    icon = <InfoOutlinedIcon />;
  } else if (executing) {
    icon = <PlayArrowIcon />;
  } else {
    icon = <CircularProgress size={13} thickness={5} sx={{ mx: 0.5, color: 'text.secondary' }} />;
  }

  const label = statusMessage || `Using ${name}`;

  return (
    <StyledChip
      icon={icon}
      label={interrupted ? `${label} — interrupted` : label}
      size="small"
      pulsing={!!executing && !completed && !interrupted}
    />
  );
}

export default ToolCallChip;
